import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryAnalytics } from '../../utils/analyticsHooks';
import { sendPermTokenRequest } from '../../utils/api/accountApi';
import { sendProfileRequest } from '../../utils/api/profileApi';
import { sendPaymentLinkRequest, sendAccountCouponRequest, type AccountDiscountCoupon } from '../../utils/api/storeApi';
import { logOut, saveTokens } from '../../utils/auth';
import { resolveAndRedirect } from '../../utils/authRedirect';
import { useDownloadToken } from '../../utils/downloadToken';
import { trackProfile } from '../../services/analytics/analyticsProvider';
import Loader from '../common/Loader';
import { getParsedParams, paramsKeys } from '../../utils/consts/params';
import { type UserProfile } from '../../utils/api/schema/profileSchema';
import { handleAuthSuccessEvents } from '../../utils/analytics/authEvents';
import { isApiError } from '../../utils/api/clearApiClient';
import { errorParams } from '../../utils/consts/errorParams';
import { grpcResponceCodes } from '../../utils/consts/apiCodes';
import ProfileLayout from './layout/ProfileLayout';
import DownloadModal from './deviceManagment/components/DownloadModal';
import DownloadLinksContextProvider from './DownloadLinksContext';
import {
  isOneOfProfileSections,
  isReferralSectionAvailable,
  type OneOfProfileSections,
  profileSections,
} from './profileSections';
import ProfileSectionSelect from './layout/SectionSelect';
import AccountDetailsSection from './accountDetails/AccountDetailsSection';
import BillingInforSection from './billingInfo/BillingInfoSection';
import DeviceManagementSection from './deviceManagment/DeviceManagementSection';
import ReferralSection from './referral/ReferralSection';
import { FloatingNotification, NotificationContextProvider } from './layout/FloatingNotification';
import fixateScrollContainerHeight from './profileLayout';
import { useActiveSectionContext } from './ActiveSectionContext';
import TvConnectedModal from './deviceManagment/components/TvConnectedModal/TvConnectedModal';
import styles from './ProfileContainer.scss';

const ProfileContainer = () => {
  const navigate = useNavigate();

  const {
    activeSection,
    setActiveSection,
    setTargetSection,
    isRouterDeviceManagementSection,
    setIsRouterDeviceManagementSection,
  } = useActiveSectionContext();

  const handleSectionChange = (section: OneOfProfileSections) => {
    if (isRouterDeviceManagementSection && section !== profileSections.DEVICES) {
      setTargetSection(section);
    } else {
      setActiveSection(section);
    }
  };

  const {
    PERMANENT_TOKEN,
    ACCESS_TOKEN,
    REFRESH_TOKEN,
    REGISTRATION_SOURCE,
    ADD_NEW_DEVICE,
    DIIA_CALLBACK_URL,
    PROFILE_SECTION,
    TV_CONNECTED,
  } = getParsedParams();

  const [profile, setProfile] = useState<UserProfile | undefined>();
  const [premiumLink, setPremiumLink] = useState('');
  const [coupon, setCoupon] = useState<AccountDiscountCoupon>({});
  const [isLoading, setIsLoading] = useState(true);

  useQueryAnalytics();

  const [isTvConnectedModalOpen, setIsTvConnectedModalOpen] = useState(TV_CONNECTED !== null);
  const closeTvConnectedModal = () => {
    setIsTvConnectedModalOpen(false);
  };
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
  const closeDeviceModal = () => {
    setIsDeviceModalOpen(false);
  };

  const { downloadToken, refreshDownloadToken } = useDownloadToken();

  useEffect(() => {
    const fetchProfile = () => {
      return Promise.all([
        refreshDownloadToken(),
        sendPaymentLinkRequest().then((data) => {
          setPremiumLink(data.data.link);
        }),
        sendAccountCouponRequest().then((res) => {
          setCoupon(res.data);
        }),
        sendProfileRequest(true).then(
          (data) => {
            setProfile(data.profile);

            handleAuthSuccessEvents(data.profile);

            if (
              !isTvConnectedModalOpen &&
              (REGISTRATION_SOURCE === 'web_funnel' || ADD_NEW_DEVICE || data.profile.devices.length === 0)
            ) {
              setIsDeviceModalOpen(true);

              void trackProfile(data.profile);
            }

            if (data.profile.marked_for_termination) {
              window.location.href = `/enter?${paramsKeys.ERROR}=${errorParams.accountTerminated}`;
            }

            window.Intercom?.('update', {
              email: data.profile.email,
              name: data.profile.full_name,
              user_id: data.profile.account_id,
            });
          },
          (error: unknown) => {
            if (
              isApiError(error) &&
              error.response?.data.code === grpcResponceCodes.INVALID_ARGUMENT &&
              error.response.data.message === 'AccountDeleted'
            ) {
              window.location.href = `/enter/${paramsKeys.ERROR}=${errorParams.accountTerminated}`;
              throw error;
            } else {
              console.error(error);
              // Dan Abramov's hack: https://github.com/facebook/react/issues/14981#issuecomment-468460187
              // eslint-disable-next-line sonarjs/no-nested-functions -- no
              setProfile(() => {
                throw error;
              });
              throw error;
            }
          },
        ),
      ]).then(() => {
        fixateScrollContainerHeight();
        if (DIIA_CALLBACK_URL) {
          handleSectionChange(profileSections.BILLING);
        } else if (PROFILE_SECTION && isOneOfProfileSections(PROFILE_SECTION)) {
          handleSectionChange(PROFILE_SECTION);
        }
        setIsLoading(false);
      });
    };

    if (PERMANENT_TOKEN) {
      // if perma token is present exchanging it for the access and refresh ones
      sendPermTokenRequest(PERMANENT_TOKEN)
        .then(() => {
          if (!resolveAndRedirect()) {
            return fetchProfile();
          }
        })
        .catch(() => {
          console.warn('Redirecting from /perm-token catch on perm');
          logOut();
        });
    } else if (ACCESS_TOKEN && REFRESH_TOKEN) {
      // saving tokens to cookies, if present
      saveTokens(ACCESS_TOKEN, REFRESH_TOKEN);
      if (!resolveAndRedirect()) {
        void navigate('/profile', { replace: true });
        fetchProfile().catch((error: unknown) => {
          console.error(error);
          console.warn('Redirecting from access-token catch on tokens');
          logOut();
        });
      }
    } else if (!resolveAndRedirect()) {
      fetchProfile().catch((error: unknown) => {
        console.error(error);
      });
    }
  }, []);

  const hasReferralSection = profile && isReferralSectionAvailable(profile);

  return (
    <ProfileLayout
      activeSection={activeSection}
      isReferralSectionAvailable={hasReferralSection}
      onSectionSelected={handleSectionChange}
    >
      <NotificationContextProvider>
        <DownloadLinksContextProvider downloadToken={downloadToken} refreshDownloadToken={refreshDownloadToken}>
          <div className={styles.container}>
            <ProfileSectionSelect
              activeSection={activeSection}
              isReferralSectionAvailable={hasReferralSection}
              className={styles.sectionSelect}
              onSectionSelected={handleSectionChange}
            />

            <main className={styles.content} id="profile-scroll-container">
              <FloatingNotification />
              {isLoading || !profile ? (
                <Loader colored centered size={30} />
              ) : (
                <div className={styles.contentContainer}>
                  {activeSection === profileSections.ACCOUNT && <AccountDetailsSection profile={profile} />}
                  {activeSection === profileSections.BILLING && (
                    <BillingInforSection profile={profile} premiumLink={premiumLink} coupon={coupon} />
                  )}
                  {activeSection === profileSections.DEVICES && (
                    <DeviceManagementSection
                      profile={profile}
                      premiumLink={premiumLink}
                      handleDeviceListUpdate={(devices) => {
                        setProfile({ ...profile, devices });
                      }}
                      onAddNewDeviceClick={() => {
                        setIsDeviceModalOpen(true);
                      }}
                    />
                  )}
                  {activeSection === profileSections.REFERAL && <ReferralSection profile={profile} />}
                </div>
              )}
            </main>
          </div>

          {!isLoading && !isTvConnectedModalOpen && (
            <DownloadModal
              isOpen={isDeviceModalOpen}
              downloadToken={downloadToken}
              requestNewDownloadToken={refreshDownloadToken}
              isSetappUser={profile?.type === 'setapp'}
              onClose={closeDeviceModal}
              onRouterSlideClick={() => {
                setIsRouterDeviceManagementSection(true);
                handleSectionChange(profileSections.DEVICES);
                setIsDeviceModalOpen(false);
              }}
            />
          )}

          {!isLoading && !isDeviceModalOpen && (
            <TvConnectedModal isOpen={isTvConnectedModalOpen} onClose={closeTvConnectedModal} />
          )}
        </DownloadLinksContextProvider>
      </NotificationContextProvider>
    </ProfileLayout>
  );
};

export default ProfileContainer;
